import React, { useState } from 'react';
import { MarketplaceHeader } from '../components/layout/MarketplaceHeader';
import { Footer } from '../components/layout/Footer';
import { RestaurantCard } from '../components/marketplace/RestaurantCard';
import { useMarketplaceData, useRestaurants } from '../lib/hooks';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { Store, Search, MapPin, Filter } from 'lucide-react';
import { useLanguage } from '../lib/i18n';
export function RestaurantListingPage() {
  const {
    categories,
    zones,
    loading: marketplaceLoading
  } = useMarketplaceData();
  const [filters, setFilters] = useState({
    zoneId: '',
    categoryId: '',
    search: ''
  });
  const {
    restaurants,
    loading: restaurantsLoading
  } = useRestaurants(filters);
  const {
    t
  } = useLanguage();
  const handleFilterChange = (key: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };
  return <div className="min-h-screen w-full flex flex-col bg-[#0B0B0B]" data-id="element-7955" data-mp="2kzjyjndn5l3j">
      <MarketplaceHeader data-id="element-7956" data-mp="2p1dtdf15nuvs" />

      <main className="flex-1" data-id="element-7957" data-mp="2p1nbyhjvezrg">
        <div className="border-b border-white/5 py-8" data-id="element-7958" data-mp="2p0lzc8n7i57m">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7959" data-mp="2baiki2qnpds7">
            <h1 className="text-3xl font-bold text-white mb-6" data-id="element-7960" data-mp="2b861rnd2qze3">
              {t('explore')}
            </h1>

            {/* Filters Bar */}
            <div className="flex flex-col md:flex-row gap-4" data-id="element-7961" data-mp="21arnnuemimoy">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-7962" data-mp="26b5x9gvvi4p3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" data-id="element-7963" data-mp="268t4s9shobxs" />
                <input type="text" placeholder={t('search_placeholder')} value={filters.search} onChange={e => handleFilterChange('search', e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/10 bg-[#141414] text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#C9A34E]/50 focus:border-[#C9A34E]/30 transition-shadow" data-id="element-7964" data-mp="1sd7msmkoasev" />
              </div>

              {/* Zone Filter */}
              <div className="w-full md:w-64 relative" data-id="element-7965" data-mp="3g8dra0m7qj2e">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" data-id="element-7966" data-mp="3g95lp842qar3" />
                <select value={filters.zoneId} onChange={e => handleFilterChange('zoneId', e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/10 bg-[#141414] text-white focus:outline-none focus:ring-2 focus:ring-[#C9A34E]/50 appearance-none cursor-pointer" data-id="element-7967" data-mp="3bbx4paunwcrf">
                  <option value="" data-id="element-7968" data-mp="3u7n9t6xw5p9j">{t('all_zones')}</option>
                  {zones.filter(zone => !['dota', 'leon-cortes'].includes(zone.slug)).map(zone => <option key={zone.id} value={zone.slug} data-id="element-7969" data-mp="y3493qmjz8zn">
                        {zone.name}
                      </option>)}
                </select>
              </div>

              {/* Category Filter */}
              <div className="w-full md:w-64 relative" data-id="element-7970" data-mp="1810st6tk3c9d">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" data-id="element-7971" data-mp="183n1xaw3hztp" />
                <select value={filters.categoryId} onChange={e => handleFilterChange('categoryId', e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-xl border border-white/10 bg-[#141414] text-white focus:outline-none focus:ring-2 focus:ring-[#C9A34E]/50 appearance-none cursor-pointer" data-id="element-7972" data-mp="1cy0pws8fh4oc">
                  <option value="" data-id="element-7973" data-mp="20py24kfshypz">{t('all_categories')}</option>
                  {categories.map(category => <option key={category.id} value={category.id} data-id="element-7974" data-mp="24r1egwympcmg">
                      {category.name}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8" data-id="element-7975" data-mp="2xgtcsum7av1n">
          {restaurantsLoading || marketplaceLoading ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7976" data-mp="3bakghmd93kpg">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} variant="card" className="h-[320px]" data-id="element-7977" data-mp="2jgs2kok1qb9i" />)}
            </div> : restaurants.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-7978" data-mp="2ekjs3ogsn68o">
              {restaurants.map(restaurant => <RestaurantCard key={restaurant.id} restaurant={restaurant} zoneName={zones.find(z => z.id === restaurant.zone_id)?.name} data-id="element-7979" data-mp="2njexiyc0kcax" />)}
            </div> : <div className="py-12" data-id="element-7980" data-mp="ba6rwjeqvxw7">
              <EmptyState icon={Store} title={t('no_restaurants')} description={t('no_restaurants_desc')} actionLabel={t('clear_filters')} onAction={() => setFilters({
            zoneId: '',
            categoryId: '',
            search: ''
          })} data-id="element-7981" data-mp="b95faai2z3cd" />
            </div>}
        </div>
      </main>

      <Footer data-id="element-7982" data-mp="yzsm0sg9fmxc" />
    </div>;
}